import React, { useState, useEffect } from "react";
import DocumentService, {
  PatientDocument,
} from "../../../../services/documentService";

interface DocumentViewerProps {
  patientDocument: PatientDocument | null;
  isOpen: boolean;
  onClose: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  patientDocument,
  isOpen,
  onClose,
}) => {
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isOpen && patientDocument) {
      loadDocument();
    } else {
      setDocumentUrl(null);
      setError(null);
    }
  }, [isOpen, patientDocument]);

  const loadDocument = async () => {
    if (!patientDocument) return;

    setIsLoading(true);
    setError(null);

    try {
      const viewData = await DocumentService.getDocumentViewData(
        patientDocument.id!
      );
      setDocumentUrl(viewData.secure_url);
    } catch (err: any) {
      setError(err.message || "Failed to load document");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!patientDocument) return;

    try {
      const { blob, filename } = await DocumentService.downloadDocument(
        patientDocument.id!
      );

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      let downloadFilename = filename;
      if (!downloadFilename.includes(".")) {
        const extension = patientDocument.file_name?.split(".").pop();
        if (extension) {
          downloadFilename += "." + extension;
        }
      }

      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`Failed to download document: ${err.message}`);
    }
  };

  if (!isOpen || !patientDocument) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "0" : "20px",
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "0" : "16px",
    width: isMobile ? "100%" : "95%",
    height: isMobile ? "100vh" : "95%",
    overflow: "hidden",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    display: "flex",
    flexDirection: "column",
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : "20px 24px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
    flexWrap: isMobile ? "wrap" : "nowrap",
    gap: isMobile ? "12px" : "0",
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "hidden",
    position: "relative",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#f59e0b", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
    };

    return {
      padding: isMobile ? "6px 12px" : "8px 16px",
      borderRadius: "6px",
      border: "none",
      fontSize: isMobile ? "12px" : "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      ...variants[variant],
    };
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                fontSize: isMobile ? "14px" : "18px",
                fontWeight: "600",
                margin: 0,
                marginBottom: "4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                wordBreak: "break-word",
              }}
            >
              <span
                style={{ fontSize: isMobile ? "16px" : "20px", flexShrink: 0 }}
              >
                {patientDocument &&
                  DocumentService.getFileTypeIcon(
                    patientDocument.mime_type,
                    patientDocument.file_name
                  )}
              </span>
              {patientDocument?.title}
            </h2>
            <div
              style={{
                fontSize: isMobile ? "11px" : "14px",
                color: "#64748b",
                lineHeight: "1.4",
              }}
            >
              {patientDocument && (
                <>
                  <span
                    style={{
                      padding: "2px 6px",
                      borderRadius: "8px",
                      fontSize: isMobile ? "9px" : "11px",
                      fontWeight: "500",
                      background:
                        DocumentService.getCategoryColor(
                          patientDocument.document_type
                        ) + "20",
                      color: DocumentService.getCategoryColor(
                        patientDocument.document_type
                      ),
                      marginRight: "8px",
                      display: isMobile ? "inline-block" : "inline",
                      marginBottom: isMobile ? "4px" : "0",
                    }}
                  >
                    {DocumentService.getCategoryIcon(
                      patientDocument.document_type
                    )}{" "}
                    {DocumentService.getCategoryLabel(
                      patientDocument.document_type
                    )}
                  </span>
                  <span style={{ display: isMobile ? "block" : "inline" }}>
                    {DocumentService.formatFileSize(patientDocument.file_size)}{" "}
                    • Uploaded{" "}
                    {DocumentService.formatDate(patientDocument.upload_date)}
                  </span>
                  {patientDocument.is_confidential && (
                    <span
                      style={{
                        marginLeft: isMobile ? "0" : "8px",
                        padding: "2px 6px",
                        borderRadius: "8px",
                        fontSize: isMobile ? "9px" : "11px",
                        fontWeight: "500",
                        background: "#fee2e2",
                        color: "#dc2626",
                        display: isMobile ? "inline-block" : "inline",
                        marginTop: isMobile ? "4px" : "0",
                      }}
                    >
                      Confidential
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: isMobile ? "6px" : "8px",
              flexShrink: 0,
              width: isMobile ? "100%" : "auto",
              order: isMobile ? 1 : 0,
            }}
          >
            <button
              onClick={handleDownload}
              style={{
                ...buttonStyle("primary"),
                flex: isMobile ? 1 : "0 0 auto",
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = "#d97706";
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = "#f59e0b";
                }
              }}
            >
              Download
            </button>
            <button
              onClick={onClose}
              style={{
                ...buttonStyle("secondary"),
                flex: isMobile ? 1 : "0 0 auto",
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = "#d1d5db";
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = "#e5e7eb";
                }
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
                gap: "16px",
                padding: isMobile ? "20px" : "0",
              }}
            >
              <div
                style={{
                  width: isMobile ? "30px" : "40px",
                  height: isMobile ? "30px" : "40px",
                  border: "4px solid #e2e8f0",
                  borderTop: "4px solid #f59e0b",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p
                style={{
                  color: "#64748b",
                  fontSize: isMobile ? "14px" : "16px",
                  textAlign: "center",
                }}
              >
                Loading document...
              </p>
            </div>
          ) : error ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
                gap: "16px",
                color: "#ef4444",
                padding: isMobile ? "20px" : "0",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: isMobile ? "32px" : "48px" }}>⚠️</div>
              <h3 style={{ fontSize: isMobile ? "16px" : "18px", margin: 0 }}>
                Failed to load document
              </h3>
              <p style={{ fontSize: isMobile ? "14px" : "16px", margin: 0 }}>
                {error}
              </p>
              <button onClick={loadDocument} style={buttonStyle("primary")}>
                Retry
              </button>
            </div>
          ) : documentUrl && patientDocument ? (
            DocumentService.canPreview(patientDocument.mime_type) ? (
              DocumentService.isImageFile(patientDocument.mime_type) ? (
                // Image viewer
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    background: "#000000",
                    padding: isMobile ? "8px" : "16px",
                  }}
                >
                  <img
                    src={documentUrl}
                    alt={patientDocument.title}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
              ) : (
                // PDF viewer
                <iframe
                  src={documentUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  title={patientDocument.title}
                />
              )
            ) : (
              // Non-previewable files
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  flexDirection: "column",
                  gap: isMobile ? "16px" : "24px",
                  background: "#f8fafc",
                  padding: isMobile ? "20px" : "40px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: isMobile ? "48px" : "64px" }}>
                  {DocumentService.getFileTypeIcon(patientDocument.mime_type)}
                </div>
                <div>
                  <h3
                    style={{
                      marginBottom: "8px",
                      color: "#1f2937",
                      fontSize: isMobile ? "16px" : "20px",
                      wordBreak: "break-word",
                    }}
                  >
                    {patientDocument.title}
                  </h3>
                  <p
                    style={{
                      color: "#6b7280",
                      marginBottom: "16px",
                      fontSize: isMobile ? "14px" : "16px",
                      lineHeight: "1.5",
                    }}
                  >
                    This file type cannot be previewed in the browser.
                  </p>
                  <button
                    onClick={handleDownload}
                    style={{
                      ...buttonStyle("primary"),
                      padding: isMobile ? "10px 20px" : "12px 24px",
                      fontSize: isMobile ? "14px" : "16px",
                    }}
                  >
                    Download to View
                  </button>
                </div>

                {/* File Details */}
                <div
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: isMobile ? "12px" : "16px",
                    maxWidth: isMobile ? "100%" : "400px",
                    width: "100%",
                  }}
                >
                  <h4
                    style={{
                      marginBottom: "12px",
                      color: "#1f2937",
                      fontSize: isMobile ? "14px" : "16px",
                    }}
                  >
                    File Details
                  </h4>
                  <div
                    style={{
                      fontSize: isMobile ? "12px" : "14px",
                      color: "#4b5563",
                      lineHeight: "1.6",
                    }}
                  >
                    <div
                      style={{ marginBottom: "8px", wordBreak: "break-word" }}
                    >
                      <strong>File Name:</strong> {patientDocument.file_name}
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <strong>Size:</strong>{" "}
                      {DocumentService.formatFileSize(
                        patientDocument.file_size
                      )}
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <strong>Type:</strong>{" "}
                      {(
                        patientDocument.file_type ||
                        patientDocument.file_name?.split(".").pop() ||
                        "Unknown"
                      ).toUpperCase()}
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <strong>Category:</strong>{" "}
                      {DocumentService.getCategoryLabel(
                        patientDocument.document_type
                      )}
                    </div>
                    {patientDocument.description && (
                      <div>
                        <strong>Description:</strong>{" "}
                        {patientDocument.description}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          ) : null}
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          @media (max-width: 768px) {
            button:hover:not(:disabled) {
              transform: none;
              box-shadow: none;
            }
          }
        `}
      </style>
    </div>
  );
};

export default DocumentViewer;
